import { useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  useAirline,
  UserAllowedAirline,
  userAirlinesQueryKey,
  enhanceUserAllowedAirlines,
} from '../contexts/AirlineContext';
import { supabase } from '../lib/supabase';
import { AirlineSelectionModal } from '../components/modals/AirlineSelectionModal';
import { shouldMobileRedirect, MOBILE_REDIRECTED_KEY } from '../utils/device';
import { GLOBAL_AIRLINE_ID, SIGNUP_URL } from '../lib/constants';

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export default function Login() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { signIn, user } = useAuth();
  const { setSelectedAirline, setSessionAirlines } = useAirline();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAirlineSelector, setShowAirlineSelector] = useState(false);
  const [availableAirlines, setAvailableAirlines] = useState<UserAllowedAirline[]>([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  // Determine post-login destination based on role and device
  const navigatePostLogin = useCallback((userMetadataRole?: string | null) => {
    if (userMetadataRole === 'trainee') {
      navigate('/trainee/dashboard');
      return;
    }
    if (shouldMobileRedirect()) {
      sessionStorage.setItem(MOBILE_REDIRECTED_KEY, 'true');
      navigate('/sessions');
    } else {
      navigate('/dashboard');
    }
  }, [navigate]);

  const onSubmit = async (data: LoginFormData) => {
    // Prevent double-submission
    if (isLoading) return;

    setIsLoading(true);
    setError(null);

    console.log('[Login] Attempting sign in for:', data.email);
    const { error: signInError } = await signIn(data.email, data.password);

    if (signInError) {
      console.error('[Login] Sign in error:', signInError);
      // Provide user-friendly error messages
      if (signInError.message.includes('rate limit')) {
        setError('Too many login attempts. Please wait a few minutes and try again.');
      } else if (signInError.message.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (signInError.message.includes('Email not confirmed')) {
        setError('Please verify your email address before logging in. Check your inbox for a confirmation link.');
      } else {
        setError(signInError.message);
      }
      setIsLoading(false);
    } else {
      console.log('[Login] Sign in successful, fetching airlines...');

      // Get current user session
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        setError('Failed to get user session');
        setIsLoading(false);
        return;
      }

      // Fetch user's airlines
      try {
        const { data: airlinesData, error: airlinesError } = await supabase
          .from('v_user_allowed_airlines')
          .select('*')
          .eq('user_id', session.user.id);

        if (airlinesError) {
          console.error('[Login] Error fetching airlines:', airlinesError);
          setError('Failed to load airlines. Please try again.');
          setIsLoading(false);
          return;
        }

        const { data: memberships } = await supabase
          .from('user_airline_memberships')
          .select('airline_id, role')
          .eq('user_id', session.user.id);

        const { data: profile } = await supabase
          .from('profiles')
          .select('default_airline_id')
          .eq('user_id', session.user.id)
          .single();

        const role = session.user.user_metadata?.role ?? null;

        const fullUserAirlines = enhanceUserAllowedAirlines(
          (airlinesData || []) as UserAllowedAirline[],
          memberships,
          profile?.default_airline_id
        );
        queryClient.setQueryData(userAirlinesQueryKey(session.user.id), fullUserAirlines);

        const enhancedAirlines = fullUserAirlines.filter(
          (airline: UserAllowedAirline) => airline.airline_id !== GLOBAL_AIRLINE_ID
        );

        console.log('[Login] Found airlines:', enhancedAirlines.length);

        if (enhancedAirlines.length === 0) {
          // No airlines - redirect to dashboard anyway (user will see "no access" message)
          console.warn('[Login] User has no airlines');
          navigatePostLogin(role);
        } else if (enhancedAirlines.length === 1) {
          // Auto-select single airline — session scope is just that one
          const firstAirline = enhancedAirlines[0]!;
          console.log('[Login] Auto-selecting single airline:', firstAirline.name);
          await setSelectedAirline(firstAirline);
          setSessionAirlines([firstAirline]);
          navigatePostLogin(role);
        } else {
          // Check for remembered preference
          const storedPref = localStorage.getItem(`airline_preference_${session.user.id}`);
          if (storedPref) {
            try {
              const { airlineId, expires } = JSON.parse(storedPref);
              const expirationDate = new Date(expires);

              // Check if preference hasn't expired
              if (expirationDate > new Date()) {
                const rememberedAirline = enhancedAirlines.find((a) => a.airline_id === airlineId);
                if (rememberedAirline) {
                  console.log('[Login] Auto-selecting remembered airline:', rememberedAirline.name);
                  await setSelectedAirline(rememberedAirline);
                  setSessionAirlines([rememberedAirline]);
                  navigatePostLogin(role);
                  return;
                }
              } else {
                // Expired - remove it
                localStorage.removeItem(`airline_preference_${session.user.id}`);
              }
            } catch (e) {
              console.error('[Login] Failed to parse stored preference:', e);
            }
          }

          // Show airline selection modal — session scope set via handleAirlineSelect
          console.log('[Login] Showing airline selector for', enhancedAirlines.length, 'airlines');
          setAvailableAirlines(enhancedAirlines);
          setShowAirlineSelector(true);
          setIsLoading(false);
        }
      } catch (err) {
        console.error('[Login] Unexpected error:', err);
        setError('An unexpected error occurred. Please try again.');
        setIsLoading(false);
      }
    }
  };

  // Handle airline selection from modal (single or multiple)
  const handleAirlineSelect = async (
    selection: UserAllowedAirline | UserAllowedAirline[],
    remember: boolean
  ) => {
    // Handle both single and multi-select
    const airlines = Array.isArray(selection) ? selection : [selection];
    const primaryAirline = airlines[0]; // Use first selection as primary

    // Safety check: ensure we have a valid airline
    if (!primaryAirline) {
      console.error('[Login] No airline selected');
      setError('Please select at least one airline');
      return;
    }

    console.log('[Login] Airlines selected:', airlines.map((a) => a.name).join(', '), 'Remember:', remember);

    if (remember && user?.id) {
      // Store in localStorage with 7-day expiration
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      localStorage.setItem(
        `airline_preference_${user.id}`,
        JSON.stringify({
          airlineId: primaryAirline.airline_id,
          expires: expirationDate.toISOString(),
        })
      );

      // Update default airline in profiles table
      await supabase
        .from('profiles')
        .update({ default_airline_id: primaryAirline.airline_id })
        .eq('user_id', user.id);
    }

    // Set primary airline and scope the session to the full selection
    await setSelectedAirline(primaryAirline);
    setSessionAirlines(airlines);
    setShowAirlineSelector(false);
    navigatePostLogin(user?.user_metadata?.role);
  };

  return (
    <div className="min-h-screen flex items-start justify-start pt-[8vh] pb-6 px-4 sm:px-6 lg:pl-[8%] auth-bg">
      {/* Dark overlay for readability */}
      <div className="fixed inset-0 bg-navy/70" aria-hidden="true" />
      <div className="relative max-w-md w-full space-y-4">
        {/* Header with Logo */}
        <div className="w-full">
          <div className="mb-4 flex justify-center">
            <img
              src="/images/wingmentor-logo.png"
              alt="WingMentor Logo"
              className="h-20 w-auto opacity-70 hover:opacity-100 transition-opacity duration-300"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <div style={{ display: 'none' }}>
              <h1 className="text-4xl font-heading font-bold text-white mb-1">WingMentor</h1>
              <p className="text-white/80 text-sm tracking-wide">COMPETENCY ASSESSMENT</p>
            </div>
          </div>
          <h2 className="mb-1 text-center text-xl font-heading font-semibold text-white">Welcome Back</h2>
          <p className="text-center text-sm text-white/80">Sign in to access your training dashboard</p>
        </div>

        {/* Form */}
        <form
          className="space-y-4 rounded-xl border-2 border-orca-elevate/15 bg-white/50 px-7 py-5 shadow-2xl backdrop-blur-sm backdrop-saturate-125 ring-1 ring-white/35"
          onSubmit={handleSubmit(onSubmit)}
        >
          {error && (
            <div className="bg-signal/10 border-l-4 border-signal text-navy px-4 py-2.5 rounded">
              <p className="font-medium text-sm">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-navy mb-1">
                Email address
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register('email')}
                className="appearance-none relative block w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="you@example.com"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-navy mb-1">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                {...register('password')}
                className="appearance-none relative block w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate sm:text-sm"
                placeholder="••••••••"
              />
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="text-sm">
            <Link to="/forgot-password" className="font-medium text-orca-elevate hover:text-orca-elevate/80">
              Forgot your password?
            </Link>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Signing in...' : 'Sign in'}
          </button>

          {/* Sign up link */}
          <div className="text-left text-sm">
            <span className="text-steel">Don't have an account? </span>
            <a
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-orca-elevate hover:text-orca-elevate/80"
            >
              Sign up
            </a>
          </div>
        </form>
      </div>

      {/* Airline Selection Modal */}
      {showAirlineSelector && availableAirlines.length > 0 && (
        <AirlineSelectionModal
          airlines={availableAirlines}
          defaultAirlineId={availableAirlines.find((a) => a.is_default)?.airline_id}
          onSelect={handleAirlineSelect}
          userName={user?.email?.split('@')[0]}
          multiSelect={true}
        />
      )}
    </div>
  );
}



